import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury/financial-position';
const PUBLISH_SNAPSHOT_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury/publish-snapshot';
const BROADCAST_SNAPSHOT_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/treasury/broadcast-snapshot';

function FinancialDashboard() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [publishing, setPublishing]   = useState(false);
  const [publishMsg, setPublishMsg]   = useState(null);
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState(null);

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = () => {
    axios.get(API, { headers })
      .then(res => { setData(res.data); setLoading(false); setLastUpdated(new Date()); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  // Writes a new row into association_snapshots — that's what the WhatsApp
  // SNAPSHOT command reads. This endpoint is intentionally public (no auth
  // header needed): it only ever writes real live figures computed
  // server-side, never accepts caller-supplied data, so there's nothing to
  // protect here beyond not letting it be spammed.
  const publishSnapshot = () => {
    setPublishing(true);
    setPublishMsg(null);
    axios.post(PUBLISH_SNAPSHOT_API)
      .then(() => {
        setPublishing(false);
        setPublishMsg({ type: 'success', text: 'Snapshot published — members can now see it via WhatsApp (reply SNAPSHOT).' });
      })
      .catch(err => {
        setPublishing(false);
        setPublishMsg({ type: 'error', text: `Failed to publish: ${err.response?.data?.error || err.message}` });
      });
  };

  // Sends the short WhatsApp nudge to every active member, based on the
  // latest published snapshot. Deliberately a separate action from
  // publishing — this one sends real messages to real people, so it stays
  // behind login (unlike publish-snapshot) and requires a deliberate click.
  // NOTE: will not actually deliver until Meta Business Verification clears
  // and the app is Published — each send fails gracefully until then, same
  // as any other WhatsApp notification in the system right now.
  const broadcastSnapshot = () => {
    if (!window.confirm('This will send a WhatsApp message to every active member. Continue?')) return;
    setBroadcasting(true);
    setBroadcastMsg(null);
    axios.post(BROADCAST_SNAPSHOT_API, {}, { headers })
      .then(res => {
        setBroadcasting(false);
        setBroadcastMsg({ type: 'success', text: `${res.data.message} (${res.data.sent} sent, ${res.data.failed} failed — failures expected until Meta approval clears.)` });
      })
      .catch(err => {
        setBroadcasting(false);
        setBroadcastMsg({ type: 'error', text: `Failed to broadcast: ${err.response?.data?.error || err.message}` });
      });
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const fmt = (n) => 'UGX ' + Number(n || 0).toLocaleString();

  const generateSnapshot = () => {
    if (!data) return;
    const doc       = new jsPDF();
    const pageW     = doc.internal.pageSize.getWidth();
    const now       = lastUpdated;
    const dateStr   = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
    const timeStr   = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const miflCredit = parseFloat(data.mifl_credit_liability || 0);

    // ── Header ──
    doc.setFillColor(26, 82, 118); doc.rect(0, 0, 210, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
    doc.text('Financial Position Snapshot', 105, 21, { align: 'center' });

    // ── Snapshot date/time ──
    doc.setTextColor(0, 0, 0); doc.setFontSize(9);
    doc.setFillColor(240, 248, 255); doc.rect(0, 30, 210, 10, 'F');
    doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
    doc.text(`Snapshot generated: ${dateStr} at ${timeStr} EAT`, 15, 37);
    doc.text('Source: KAMS — Kigezi Association Management System', pageW - 15, 37, { align: 'right' });

    // ── Net Worth Banner ──
    doc.setFillColor(26, 82, 118); doc.rect(15, 45, 180, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text('ASSOCIATION NET WORTH', 105, 52, { align: 'center' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(22);
    doc.text(fmt(data.true_net_position), 105, 64, { align: 'center' });

    // ── Net Worth Breakdown ──
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(0, 0, 0);
    let bY = 80;
    autoTable(doc, {
      startY: bY,
      head: [['Component', 'Amount (UGX)', 'Type']],
      body: [
        ['Cash in Treasury', Number(data.treasury_cash).toLocaleString(), 'Asset'],
        ['Loan Principal Outstanding', Number(data.loan_receivables).toLocaleString(), 'Asset'],
        ['Interest Receivable', Number(data.interest_receivables).toLocaleString(), 'Asset'],
        ['MIFL Arrears Receivable', Number(data.mifl_receivables).toLocaleString(), 'Asset'],
        ['Fines Receivable', Number(data.fines_receivable || 0).toLocaleString(), 'Asset'],
        ['MIFL Member Savings', '(' + Number(miflCredit).toLocaleString() + ')', 'Liability'],
        ['NET WORTH', Number(data.true_net_position).toLocaleString(), ''],
      ],
      styles: { fontSize: 10 },
      headStyles: { fillColor: [26, 82, 118] },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'center' } },
      didParseCell: (d) => {
        if (d.row.index === 5 && d.section === 'body') d.cell.styles.textColor = [192, 57, 43];
        if (d.row.index === 6 && d.section === 'body') { d.cell.styles.fontStyle = 'bold'; d.cell.styles.fillColor = [234, 244, 251]; }
      },
      margin: { left: 15, right: 15 }
    });

    // ── Treasury Accounts ──
    let y2 = doc.lastAutoTable.finalY + 8;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(26, 82, 118);
    doc.text('Treasury Accounts', 15, y2); y2 += 4;
    autoTable(doc, {
      startY: y2,
      head: [['Account', 'Balance (UGX)']],
      body: (data.accounts || []).map(a => [a.account_name, Number(a.balance).toLocaleString()]),
      foot: [['Total Cash', Number(data.treasury_cash).toLocaleString()]],
      styles: { fontSize: 10 }, headStyles: { fillColor: [30, 132, 73] },
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 110 }
    });

    // ── Member Status (side by side with accounts) ──
    const ms = data.member_status || {};
    autoTable(doc, {
      startY: y2,
      head: [['Member Status', 'Count']],
      body: [
        ['Active', ms.active || 0],
        ['Suspended', ms.suspended || 0],
        ['Expelled', ms.expelled || 0],
      ],
      foot: [['Total', ms.total || 0]],
      styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
      footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 110, right: 15 }
    });

    // ── Loan Interest Tracker ──
    const finalY = Math.max(doc.lastAutoTable.finalY, doc.lastAutoTable.finalY) + 8;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(183, 119, 13);
    doc.text('Loan Interest Tracker', 15, finalY + 4);
    autoTable(doc, {
      startY: finalY + 8,
      head: [['Metric', 'Amount (UGX)']],
      body: [
        ['Active Loans', String(data.active_loans_count || 0) + ' loans'],
        ['Interest at Full Maturity', Number(data.total_interest_at_maturity || 0).toLocaleString()],
        ['Interest Accrued to Date', Number(data.total_interest_accrued_to_date || 0).toLocaleString()],
        ['Interest Collected', Number(data.total_interest_paid || 0).toLocaleString()],
        ['Interest Receivable (Unpaid)', Number(data.total_interest_receivable || 0).toLocaleString()],
        ['Daily Accrual Rate', '+' + Number(data.total_daily_accrual || 0).toLocaleString() + ' per day'],
      ],
      styles: { fontSize: 10 }, headStyles: { fillColor: [183, 119, 13] },
      columnStyles: { 1: { halign: 'right' } },
      margin: { left: 15, right: 15 }
    });

    // ── Footer ──
    const ph = doc.internal.pageSize.height;
    doc.setFillColor(44, 62, 80); doc.rect(0, ph - 18, 210, 18, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text('Kigezi Association — Confidential Financial Snapshot', 105, ph - 10, { align: 'center' });
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
    doc.text('Generated by KAMS — Kigezi Association Management System | For internal distribution only', 105, ph - 5, { align: 'center' });

    doc.save(`KigeziAssociation_NetWorth_${now.toISOString().slice(0, 10)}.pdf`);
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading financial position...</div>;
  if (error)   return <div style={{ padding: '40px', color: '#e74c3c' }}>Error: {error}</div>;

  const netPositive          = parseFloat(data.true_net_position) >= 0;
  const hoursElapsed         = new Date().getHours() + new Date().getMinutes() / 60;
  const projectedTodayInterest = ((data.total_daily_accrual || 0) / 24) * hoursElapsed;
  const miflCredit           = parseFloat(data.mifl_credit_liability || 0);

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h2 style={{ color: '#2c3e50', margin: 0 }}>Financial Dashboard</h2>
          <p style={{ color: '#888', margin: '4px 0 0', fontSize: '13px' }}>
            Live association net worth — cash + loans + interest + MIFL arrears − MIFL member savings
          </p>
        </div>
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#95a5a6' }}>
          Updated: {lastUpdated.toLocaleTimeString('en-GB')}<br />
          <div style={{ display: 'flex', gap: '6px', marginTop: '4px', justifyContent: 'flex-end' }}>
            <button onClick={publishSnapshot} disabled={publishing} style={{ background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: publishing ? 'default' : 'pointer', fontSize: '11px', fontWeight: '600', opacity: publishing ? 0.7 : 1 }}>
              {publishing ? 'Publishing…' : '📣 Publish Snapshot'}
            </button>
            <button onClick={broadcastSnapshot} disabled={broadcasting} title="Not yet deliverable — blocked on Meta Business Verification / app Publish status" style={{ background: '#8e44ad', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: broadcasting ? 'default' : 'pointer', fontSize: '11px', fontWeight: '600', opacity: broadcasting ? 0.7 : 1 }}>
              {broadcasting ? 'Broadcasting…' : '📢 Broadcast to WhatsApp'}
            </button>
            <button onClick={generateSnapshot} style={{ background: '#1a5276', color: 'white', border: 'none', borderRadius: '4px', padding: '3px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>📄 Download Snapshot</button>
            <button onClick={fetchData} style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', padding: '3px 8px', cursor: 'pointer', fontSize: '11px', color: '#666' }}>↻ Refresh</button>
          </div>
        </div>
      </div>

      {publishMsg && (
        <div style={{
          marginBottom: '16px', padding: '8px 14px', borderRadius: '6px', fontSize: '12px',
          background: publishMsg.type === 'success' ? '#eafaf1' : '#fdecea',
          color: publishMsg.type === 'success' ? '#1e7e45' : '#c0392b',
          border: `1px solid ${publishMsg.type === 'success' ? '#a3e4c1' : '#f5b7b1'}`,
        }}>
          {publishMsg.text}
        </div>
      )}

      {broadcastMsg && (
        <div style={{
          marginBottom: '16px', padding: '8px 14px', borderRadius: '6px', fontSize: '12px',
          background: broadcastMsg.type === 'success' ? '#f4ecf7' : '#fdecea',
          color: broadcastMsg.type === 'success' ? '#6c3483' : '#c0392b',
          border: `1px solid ${broadcastMsg.type === 'success' ? '#d2b4de' : '#f5b7b1'}`,
        }}>
          {broadcastMsg.text}
        </div>
      )}

      {/* TRUE NET WORTH */}
      <div style={{
        background: netPositive ? 'linear-gradient(135deg, #1a5276, #2980b9)' : 'linear-gradient(135deg, #922b21, #e74c3c)',
        borderRadius: '10px', padding: '24px 32px', marginBottom: '24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', flexWrap: 'wrap', gap: '16px'
      }}>
        <div>
          <div style={{ fontSize: '12px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px' }}>Association Net Worth</div>
          <div style={{ fontSize: '40px', fontWeight: '800', marginTop: '4px' }}>{fmt(data.true_net_position)}</div>
          <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '8px', lineHeight: '1.8' }}>
            Cash {fmt(data.treasury_cash)}<br />
            + Loans Outstanding {fmt(data.loan_receivables)}<br />
            + Interest Receivable {fmt(data.interest_receivables)}<br />
            + MIFL Arrears {fmt(data.mifl_receivables)}<br />
            + Fines Receivable {fmt(data.fines_receivable || 0)}<br />
            {miflCredit > 0 && (
              <span style={{ color: '#f1948a' }}>− MIFL Member Savings {fmt(miflCredit)}</span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '4px' }}>Daily Interest Accruing</div>
          <div style={{ fontSize: '28px', fontWeight: '700', color: '#f9ca24' }}>
            +{fmt(Math.round(data.total_daily_accrual || 0))}
          </div>
          <div style={{ fontSize: '11px', opacity: 0.6 }}>per day across {data.active_loans_count || 0} active loan(s)</div>
        </div>
      </div>

      {/* SIX COMPONENT CARDS */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {[
          { label: 'Cash in Treasury',       val: data.treasury_cash,           color: '#1e8449', note: 'Bank + cash balances' },
          { label: 'Loan Principal',          val: data.loan_receivables,        color: '#1a5276', note: 'Active loan balances' },
          { label: 'Interest Receivable',     val: data.interest_receivables,    color: '#b7770d', note: 'Unpaid accrued interest' },
          { label: 'MIFL Arrears',            val: data.mifl_receivables,        color: '#6c3483', note: 'All unpaid member obligations' },
          { label: 'Fines Receivable',        val: data.fines_receivable || 0,   color: '#af601a', note: 'Issued, unescalated fines' },
          { label: 'MIFL Member Savings',   val: miflCredit,                   color: '#c0392b', note: 'Money of members held in MIFL' },
        ].map((card, i) => (
          <div key={i} style={{ background: card.color, color: 'white', borderRadius: '8px', padding: '18px 20px', minWidth: '180px', flex: 1 }}>
            <div style={{ fontSize: '11px', opacity: 0.8, textTransform: 'uppercase', marginBottom: '4px' }}>{card.label}</div>
            <div style={{ fontSize: '20px', fontWeight: '700' }}>
              {i === 5 && miflCredit > 0 ? '−' : ''}{fmt(card.val)}
            </div>
            <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '4px' }}>{card.note}</div>
          </div>
        ))}
      </div>

      {/* INTEREST TRACKER */}
      <div style={{ background: 'white', border: '2px solid #f39c12', borderRadius: '10px', padding: '20px 24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, color: '#b7770d', fontSize: '15px' }}>📈 Loan Interest Tracker</h3>
          <div style={{ fontSize: '12px', color: '#95a5a6' }}>
            Projected today: <strong style={{ color: '#f39c12' }}>+{fmt(Math.round(projectedTodayInterest))}</strong> ({hoursElapsed.toFixed(1)}hrs elapsed)
          </div>
        </div>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {[
            { label: 'Interest at Full Maturity', val: data.total_interest_at_maturity,     note: 'If all loans run to term end', bg: '#fef9e7', border: '#f39c12', text: '#b7770d' },
            { label: 'Interest Accrued to Date',  val: data.total_interest_accrued_to_date, note: 'Based on months elapsed',      bg: '#fff3cd', border: '#f39c12', text: '#856404' },
            { label: 'Interest Collected',        val: data.total_interest_paid,            note: 'Actually received',            bg: '#d4edda', border: '#27ae60', text: '#155724' },
            { label: 'Interest Receivable',       val: data.total_interest_receivable,      note: 'Accrued but not yet paid',     bg: '#fde8e8', border: '#e74c3c', text: '#922b21' },
            { label: 'Daily Accrual Rate',        val: data.total_daily_accrual,            note: 'Association earns every day',  bg: '#e8f4f8', border: '#2980b9', text: '#1a5276' },
          ].map((c, i) => (
            <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: '8px', padding: '14px 16px', minWidth: '150px', flex: 1 }}>
              <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '4px' }}>{c.label}</div>
              <div style={{ fontSize: '17px', fontWeight: '700', color: '#2c3e50', marginBottom: '4px' }}>{fmt(Math.round(c.val || 0))}</div>
              <div style={{ fontSize: '11px', color: '#888' }}>{c.note}</div>
            </div>
          ))}
        </div>
        {data.total_interest_accrued_to_date > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              <span>Collection rate — {Math.round((data.total_interest_paid || 0) / data.total_interest_accrued_to_date * 100)}% of accrued interest collected</span>
              <span>{fmt(Math.round(data.total_interest_paid || 0))} / {fmt(Math.round(data.total_interest_accrued_to_date))}</span>
            </div>
            <div style={{ background: '#ecf0f1', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ background: 'linear-gradient(90deg, #27ae60, #2ecc71)', width: `${Math.min(100, (data.total_interest_paid || 0) / data.total_interest_accrued_to_date * 100)}%`, height: '100%', borderRadius: '4px' }} />
            </div>
          </div>
        )}
      </div>

      {/* TREASURY ACCOUNTS + MEMBER STATUS */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '260px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: '15px' }}>Treasury Accounts</h3>
          {data.accounts && data.accounts.map((acc, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '14px', color: '#444' }}>{acc.account_name}</span>
              <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e8449' }}>{fmt(acc.balance)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50' }}>Total Cash</span>
            <span style={{ fontWeight: '800', fontSize: '15px', color: '#1a5276' }}>{fmt(data.treasury_cash)}</span>
          </div>
        </div>

        <div style={{ flex: 1, minWidth: '260px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: '15px' }}>Member Status</h3>
          {data.member_status && [
            { label: '✅ Active',    value: data.member_status.active,    color: '#1e8449' },
            { label: '⚠️ Suspended', value: data.member_status.suspended, color: '#e74c3c' },
            { label: '🚫 Expelled',  value: data.member_status.expelled,  color: '#922b21' },
          ].map((row, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0' }}>
              <span style={{ fontSize: '14px', color: '#444' }}>{row.label}</span>
              <span style={{ fontWeight: '700', color: row.color }}>{row.value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0 0' }}>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#2c3e50' }}>Total Members</span>
            <span style={{ fontWeight: '800', fontSize: '15px', color: '#2c3e50' }}>{data.member_status?.total}</span>
          </div>
        </div>
      </div>

      {/* RECENT TRANSACTIONS + EXPENSES */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: '320px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: '15px' }}>Recent Treasury Transactions</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Date', 'Account', 'Type', 'Amount (UGX)', 'Description'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: h === 'Amount (UGX)' ? 'right' : 'left', color: '#666', fontWeight: '600' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.recent_transactions?.length > 0 ? data.recent_transactions.map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '8px', color: '#444' }}>{new Date(t.transaction_date).toLocaleDateString('en-GB')}</td>
                  <td style={{ padding: '8px', color: '#444' }}>{t.account_name}</td>
                  <td style={{ padding: '8px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: t.direction === 'IN' ? '#d5f5e3' : '#fde8e8', color: t.direction === 'IN' ? '#1e8449' : '#c0392b' }}>
                      {t.direction}
                    </span>
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: t.direction === 'IN' ? '#1e8449' : '#c0392b' }}>
                    {Number(t.amount).toLocaleString()}
                  </td>
                  <td style={{ padding: '8px', color: '#666' }}>{t.description}</td>
                </tr>
              )) : (
                <tr><td colSpan="5" style={{ padding: '16px', textAlign: 'center', color: '#aaa' }}>No transactions yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ flex: 1, minWidth: '240px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px', color: '#2c3e50', fontSize: '15px' }}>Recent Expenses</h3>
          {data.recent_expenses?.length > 0 ? data.recent_expenses.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}>
              <div>
                <div style={{ color: '#444' }}>{e.description}</div>
                <div style={{ color: '#aaa', fontSize: '11px' }}>{new Date(e.expense_date).toLocaleDateString('en-GB')}</div>
              </div>
              <div style={{ fontWeight: '700', color: '#c0392b', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                -{Number(e.amount).toLocaleString()}
              </div>
            </div>
          )) : (
            <div style={{ color: '#aaa', fontSize: '13px', padding: '8px 0' }}>No expenses yet</div>
          )}
        </div>
      </div>

      <div style={{ marginTop: '24px', padding: '12px 16px', background: '#eaf4fb', borderLeft: '4px solid #2980b9', borderRadius: '4px', fontSize: '12px', color: '#555' }}>
        <strong>Net Worth Formula:</strong> Cash in Treasury + Loan Principal Outstanding + Interest Receivable + MIFL Arrears − MIFL Member Savings. MIFL Member Savings = overpayments held by the association on behalf of members (MCN − CUN). Updated every 60 seconds.
      </div>
    </div>
  );
}

export default FinancialDashboard;